/**
 * Normalizes the case of a string by capitalizing the first character and converting the rest to lowercase.
 *
 * @param {string} str - The input string to normalize.
 * @returns {string} The normalized string with the first character in uppercase and the rest in lowercase.
 */
export const normalizeStringCase = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Returns the complete day of the week string that matches the given input.
 * If the input is a partial or case-insensitive day name, it normalizes and matches it to the full day name.
 * If no match is found, returns the original input.
 *
 * @param {string} day - The partial or full name of the day (case-insensitive).
 * @returns {string} The complete day of the week string, or the original input if no match is found.
 */
export const completeDayOfWeekString = (day) => {
  const normalizedDay = normalizeStringCase(day);
  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  return daysOfWeek.find((d) => d.startsWith(normalizedDay)) || day;
};

/**
 * Formats a given input string as a US phone number (XXX-XXX-XXXX).
 * Non-numeric characters are removed and only the first 10 digits are considered.
 *
 * @param {string} input - The input string to format as a phone number.
 * @returns {string} The formatted phone number string.
 */
export const formatPhoneNumber = (input) => {
  const MAX_DIGITS = 10;
  const digits = input.replace(/[^0-9]/g, "").slice(0, MAX_DIGITS);

  if (digits.length > 6) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return digits;
};

/**
 * Checks if the given input is a valid phone number consisting of exactly 10 digits.
 *
 * Non-numeric characters are ignored when counting digits.
 *
 * @param {string} input - The input string to validate as a phone number.
 * @returns {boolean} Returns true if the input contains exactly 10 digits, false otherwise.
 */
export const isPhoneValid = (input) => input.replace(/[^0-9]/g, "").length === 10;
